import React, { useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Select,
  Input,
  Table,
  Tag,
  Drawer,
  Statistic,
  message,
} from 'antd';
import {
  ApiOutlined,
  LinkOutlined,
  DisconnectOutlined,
  SettingOutlined,
  ReloadOutlined,
  PlusOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

const WhiteLabelIntegrations = () => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const integrations = useMemo(() => ([
    { id: 'geotab', name: 'Geotab', category: 'Telematics', status: 'Connected', accounts: 2, scopes: ['devices', 'trips'], description: 'Vehicle telematics and trip data.' },
    { id: 'stripe', name: 'Stripe', category: 'Billing', status: 'Available', accounts: 0, scopes: ['payments', 'invoices'], description: 'Payments, subscriptions, and invoicing.' },
    { id: 'hubspot', name: 'HubSpot', category: 'CRM', status: 'Available', accounts: 0, scopes: ['contacts'], description: 'Sync clients and contacts.' },
    { id: 'slack', name: 'Slack', category: 'Collaboration', status: 'Connected', accounts: 1, scopes: ['webhooks'], description: 'Notifications and alerts to channels.' },
    { id: 'google-ads', name: 'Google Ads', category: 'Ads', status: 'Error', accounts: 1, scopes: ['campaigns', 'reports'], description: 'Ad campaigns and reporting.' },
  ]), []);

  const categories = ['all', 'Telematics', 'Billing', 'CRM', 'Collaboration', 'Ads'];
  const statuses = ['all', 'Connected', 'Available', 'Error'];

  const filtered = useMemo(() => (
    integrations.filter(i => (
      (!search || i.name.toLowerCase().includes(search.toLowerCase())) &&
      (category === 'all' || i.category === category) &&
      (status === 'all' || i.status === status)
    ))
  ), [integrations, search, category, status]);

  const connect = (record) => {
    // TODO: Kick off OAuth/connection flow
    message.success(`Started connection for ${record.name}`);
  };

  const disconnect = (record) => {
    // TODO: Call backend to revoke and disconnect
    message.info(`Disconnected ${record.name}`);
  };

  const openDetails = (record) => {
    setSelected(record);
    setDrawerOpen(true);
  };

  const columns = [
    { title: 'Integration', dataIndex: 'name', key: 'name', render: (t, r) => <Button type="link" onClick={() => openDetails(r)}>{t}</Button> },
    { title: 'Category', dataIndex: 'category', key: 'category', width: 150 },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 140, render: (s) => (
      <Tag color={s === 'Connected' ? 'green' : s === 'Error' ? 'red' : 'default'}>{s}</Tag>
    ) },
    { title: 'Accounts', dataIndex: 'accounts', key: 'accounts', width: 120 },
    { title: 'Actions', key: 'actions', width: 240, render: (_, r) => (
      <Space>
        <Button icon={<SettingOutlined />} onClick={() => openDetails(r)}>Configure</Button>
        {r.status === 'Connected' ? (
          <Button danger icon={<DisconnectOutlined />} onClick={() => disconnect(r)}>Disconnect</Button>
        ) : (
          <Button type="primary" icon={<LinkOutlined />} onClick={() => connect(r)}>Connect</Button>
        )}
      </Space>
    )},
  ];

  const kpis = useMemo(() => {
    const total = integrations.length;
    const connected = integrations.filter(i => i.status === 'Connected').length;
    const errors = integrations.filter(i => i.status === 'Error').length;
    return { total, connected, errors };
  }, [integrations]);

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>WhiteLabel Integrations</Title>
          <Text type="secondary">Connect and manage integrations available to your agency clients.</Text>
        </div>

        {/* KPIs */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Space>
                <ApiOutlined style={{ fontSize: 20 }} />
                <Statistic title="Total Integrations" value={kpis.total} />
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Space>
                <LinkOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                <Statistic title="Connected" value={kpis.connected} />
              </Space>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Space>
                <ReloadOutlined rotate={90} style={{ fontSize: 20, color: '#ff4d4f' }} />
                <Statistic title="Errors" value={kpis.errors} />
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Toolbar */}
        <Card bodyStyle={{ padding: 12 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={8}>
              <Input placeholder="Search integrations" value={search} onChange={(e) => setSearch(e.target.value)} />
            </Col>
            <Col xs={24} md={6}>
              <Select value={category} onChange={setCategory} style={{ width: '100%' }}>
                {categories.map(c => <Select.Option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</Select.Option>)}
              </Select>
            </Col>
            <Col xs={24} md={6}>
              <Select value={status} onChange={setStatus} style={{ width: '100%' }}>
                {statuses.map(s => <Select.Option key={s} value={s}>{s === 'all' ? 'All Statuses' : s}</Select.Option>)}
              </Select>
            </Col>
            <Col xs={24} md={4} style={{ textAlign: 'right' }}>
              <Space>
                <Button icon={<PlusOutlined />}>Add Custom</Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        <Card title="Integrations" bodyStyle={{ padding: 0 }}>
          <Table rowKey="id" columns={columns} dataSource={filtered} pagination={{ pageSize: 8 }} />
        </Card>

        {/* Details Drawer */}
        <Drawer
          title={selected ? selected.name : 'Integration Details'}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={520}
        >
          {selected && (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Text type="secondary">Category</Text>
              <Text>{selected.category}</Text>
              <Text type="secondary">Status</Text>
              <Tag color={selected.status === 'Connected' ? 'green' : selected.status === 'Error' ? 'red' : 'default'}>{selected.status}</Tag>
              <Text type="secondary">Description</Text>
              <Text>{selected.description}</Text>
              <Text type="secondary">Scopes</Text>
              <Space wrap>
                {selected.scopes.map(s => <Tag key={s}>{s}</Tag>)}
              </Space>
              <Space style={{ marginTop: 12 }}>
                {selected.status === 'Connected' ? (
                  <Button danger icon={<DisconnectOutlined />} onClick={() => disconnect(selected)}>Disconnect</Button>
                ) : (
                  <Button type="primary" icon={<LinkOutlined />} onClick={() => connect(selected)}>Connect</Button>
                )}
                <Button icon={<SettingOutlined />}>Manage</Button>
              </Space>
            </Space>
          )}
        </Drawer>
      </Space>
    </div>
  );
};

export default WhiteLabelIntegrations;
